import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import type { EntraBrowserAuthConfig } from './config.js';

export type EntraClientErrorKind = 'CANCELLED' | 'INTERACTION_REQUIRED' | 'AUTHENTICATION_FAILED';

export class EntraClientError extends Error {
  constructor(readonly kind: EntraClientErrorKind) {
    super(kind);
    this.name = 'EntraClientError';
  }
}

export interface EntraAccountSnapshot {
  homeAccountId: string;
  localAccountId: string;
  tenantId: string;
  username: string;
  name?: string;
  idTokenClaims?: Readonly<Record<string, unknown>>;
}

export interface EntraAuthenticationSnapshot {
  account: EntraAccountSnapshot;
  idTokenClaims: Readonly<Record<string, unknown>>;
  identityToken: string;
}

export interface EntraClientAdapter {
  initialize(): Promise<void>;
  handleRedirect(): Promise<EntraAuthenticationSnapshot | null>;
  getAccounts(): readonly EntraAccountSnapshot[];
  setActiveAccount(homeAccountId: string): void;
  loginRedirect(): Promise<void>;
  logoutRedirect(homeAccountId: string): Promise<void>;
  acquireTokenSilent(homeAccountId: string): Promise<EntraAuthenticationSnapshot>;
}

export class MsalBrowserClientAdapter implements EntraClientAdapter {
  private readonly application: PublicClientApplication;
  private readonly scopes: readonly string[];
  private readonly redirectUri: string;

  constructor(config: EntraBrowserAuthConfig) {
    this.scopes = config.scopes;
    this.redirectUri = config.redirectUri;
    this.application = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: `https://login.microsoftonline.com/${config.tenantId}`,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.redirectUri,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
      },
      system: {
        loggerOptions: {
          piiLoggingEnabled: false,
          loggerCallback: () => {},
        },
      },
    });
  }

  async initialize(): Promise<void> {
    await this.safely(() => this.application.initialize());
  }

  async handleRedirect(): Promise<EntraAuthenticationSnapshot | null> {
    const result = await this.safely(() => this.application.handleRedirectPromise({
      navigateToLoginRequestUrl: true,
    }));
    return result === null ? null : snapshotResult(result);
  }

  getAccounts(): readonly EntraAccountSnapshot[] {
    return this.application.getAllAccounts().map(snapshotAccount);
  }

  setActiveAccount(homeAccountId: string): void {
    this.application.setActiveAccount(this.requireAccount(homeAccountId));
  }

  async loginRedirect(): Promise<void> {
    await this.safely(() => this.application.loginRedirect({
      scopes: [...this.scopes],
      redirectUri: this.redirectUri,
    }));
  }

  async logoutRedirect(homeAccountId: string): Promise<void> {
    await this.safely(() => this.application.logoutRedirect({
      account: this.requireAccount(homeAccountId),
      postLogoutRedirectUri: this.redirectUri,
    }));
  }

  async acquireTokenSilent(homeAccountId: string): Promise<EntraAuthenticationSnapshot> {
    const result = await this.safely(() => this.application.acquireTokenSilent({
      account: this.requireAccount(homeAccountId),
      scopes: [...this.scopes],
      forceRefresh: false,
    }));
    return snapshotResult(result);
  }

  private requireAccount(homeAccountId: string): AccountInfo {
    const account = this.application.getAllAccounts().find((candidate) => candidate.homeAccountId === homeAccountId);
    if (account === undefined) throw new EntraClientError('INTERACTION_REQUIRED');
    return account;
  }

  private async safely<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      throw normalizeMsalError(error);
    }
  }
}

function snapshotResult(result: AuthenticationResult): EntraAuthenticationSnapshot {
  if (result.account === null) throw new EntraClientError('AUTHENTICATION_FAILED');
  return {
    account: snapshotAccount(result.account),
    idTokenClaims: asClaims(result.idTokenClaims),
    identityToken: result.idToken,
  };
}

function snapshotAccount(account: AccountInfo): EntraAccountSnapshot {
  const optional = {
    ...(account.name === undefined ? {} : { name: account.name }),
    ...(account.idTokenClaims === undefined ? {} : { idTokenClaims: asClaims(account.idTokenClaims) }),
  };
  return {
    homeAccountId: account.homeAccountId,
    localAccountId: account.localAccountId,
    tenantId: account.tenantId,
    username: account.username,
    ...optional,
  };
}

function asClaims(value: object): Readonly<Record<string, unknown>> {
  return value as Readonly<Record<string, unknown>>;
}

function normalizeMsalError(error: unknown): EntraClientError {
  if (error instanceof EntraClientError) return error;
  if (error instanceof InteractionRequiredAuthError) return new EntraClientError('INTERACTION_REQUIRED');
  if (error instanceof BrowserAuthError && (
    error.errorCode === BrowserAuthErrorCodes.userCancelled
    || error.errorCode === BrowserAuthErrorCodes.interactionInProgressCancelled
  )) {
    return new EntraClientError('CANCELLED');
  }
  return new EntraClientError('AUTHENTICATION_FAILED');
}
